import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import type { TransactionContext } from "@telemetry/runTransaction";

type UploadedDocument = {
  documentUrl: string;
  documentType: string;
  documentName: string;
  idx?: number;
};

type SingleUploadParams = {
  file: File;
  documentType: string;
  idx?: number;
};

type MultipleUploadParams = {
  files: File[];
  documentType: string;
  startIdx: number;
};

type UploadParams = SingleUploadParams | MultipleUploadParams;

type BatchUploadSuccessData = {
  documents: UploadedDocument[];
  startIdx: number;
};

type UploadErrorMeta = {
  documentType: string;
  idx?: number;
  startIdx?: number;
  count: number;
  fileName?: string;
  fileNames: string[];
};

type SingleUseUploadDocumentOptions = {
  multiple?: false;
  transaction?: TransactionContext;
  handleUploadSuccess?: (data: UploadedDocument) => void;
  handleUploadError?: (error: Error, meta: UploadErrorMeta) => void;
};

type MultipleUseUploadDocumentOptions = {
  multiple: true;
  transaction?: TransactionContext;
  handleUploadSuccess?: (data: BatchUploadSuccessData) => void;
  handleUploadError?: (error: Error, meta: UploadErrorMeta) => void;
};

type UseUploadDocumentOptions = SingleUseUploadDocumentOptions | MultipleUseUploadDocumentOptions;

type LegacySuccessHandler = (data: UploadedDocument) => void;

type MutationSuccessData = UploadedDocument | BatchUploadSuccessData;

type UseUploadDocumentReturn = UseMutationResult<MutationSuccessData, Error, UploadParams> & {
  isLoading: boolean;
};

const isOptionsObject = (
  optionsOrSuccess?: UseUploadDocumentOptions | LegacySuccessHandler,
): optionsOrSuccess is UseUploadDocumentOptions =>
  typeof optionsOrSuccess === "object" && optionsOrSuccess !== null;

const uploadOneDocument = async (
  { file, documentType, idx }: SingleUploadParams,
  transactionId?: string,
): Promise<UploadedDocument> => {
  const payloadForSasToken = {
    prefix: SasTokenPrefix.VESSEL_NOMINATION,
    contentType: file.type || null,
    privilege: SasTokenPrivilege.WRITE,
    blobUrl: null,
  };
  const { blobUrl, sasToken } = await getSasToken(
    payloadForSasToken,
    ...(transactionId ? [transactionId] : []),
  );
  const [result] = await uploadFileToAzureBlob({
    file,
    blobUrl,
    sasToken,
    certificateType: documentType,
  });
  const documentUrl = result?.documentUrl ?? blobUrl;

  return { documentUrl, documentType, idx, documentName: file.name };
};

const isMultipleUpload = (variables: UploadParams): variables is MultipleUploadParams =>
  "files" in variables;

export function useUploadDocument(
  onSuccess?: LegacySuccessHandler,
  onErrorLegacy?: (documentType: string) => void,
): UseUploadDocumentReturn;
export function useUploadDocument(options: SingleUseUploadDocumentOptions): UseUploadDocumentReturn;
export function useUploadDocument(
  options: MultipleUseUploadDocumentOptions,
): UseUploadDocumentReturn;
export function useUploadDocument(
  optionsOrSuccess?: UseUploadDocumentOptions | LegacySuccessHandler,
  onErrorLegacy?: (documentType: string) => void,
): UseUploadDocumentReturn {
  const handleUploadSuccess = isOptionsObject(optionsOrSuccess)
    ? optionsOrSuccess.handleUploadSuccess
    : optionsOrSuccess;

  const handleUploadError = isOptionsObject(optionsOrSuccess)
    ? optionsOrSuccess.handleUploadError
    : (_error: Error, meta: UploadErrorMeta) => onErrorLegacy?.(meta.documentType);

  const transaction = isOptionsObject(optionsOrSuccess) ? optionsOrSuccess.transaction : undefined;

  const mutation = useMutation({
    mutationFn: async (variables: UploadParams) => {
      const upload = async (transactionId?: string) => {
        if (isMultipleUpload(variables)) {
          const documents: UploadedDocument[] = [];

          for (const [fileOffset, file] of variables.files.entries()) {
            const document = await uploadOneDocument(
              {
                file,
                documentType: variables.documentType,
                idx: variables.startIdx + fileOffset,
              },
              transactionId,
            );

            documents.push(document);
          }

          return {
            documents,
            startIdx: variables.startIdx,
          } satisfies BatchUploadSuccessData;
        }

        return uploadOneDocument(variables, transactionId);
      };

      return transaction
        ? runTransaction(transaction, (transactionId) => upload(transactionId))
        : upload();
    },
    onSuccess: (data: MutationSuccessData) => {
      (handleUploadSuccess as ((data: MutationSuccessData) => void) | undefined)?.(data);
    },
    onError: (error: Error, variables: UploadParams) => {
      if (isMultipleUpload(variables)) {
        handleUploadError?.(error, {
          documentType: variables.documentType,
          startIdx: variables.startIdx,
          count: variables.files.length,
          fileNames: variables.files.map((file) => file.name),
        });

        return;
      }

      handleUploadError?.(error, {
        documentType: variables.documentType,
        idx: variables.idx,
        count: 1,
        fileName: variables.file.name,
        fileNames: [variables.file.name],
      });
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending,
  };
}

export type { BatchUploadSuccessData, UploadErrorMeta, UploadedDocument };
